'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useRouter } from 'next/navigation';
import { trackLogin, trackLogout, trackSignUp, setUserProperties } from '@/lib/analytics';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [integratorProfile, setIntegratorProfile] = useState<any>(null);
  const [bearsToken, setBearsTokenState] = useState<string | null>(null);
  const router = useRouter();

  const getGeolocationSilently = (): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      if (typeof window === 'undefined' || !navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = parseFloat(position.coords.latitude.toFixed(6));
          const lng = parseFloat(position.coords.longitude.toFixed(6));
          localStorage.setItem('user_lat', lat.toString());
          localStorage.setItem('user_lng', lng.toString());
          resolve({ lat, lng });
        },
        () => resolve(null)
      );
    });
  };

  const getBearsToken = (): string | null => {
    return null; // Token is now managed securely via HttpOnly cookies
  };

  const fetchIntegratorProfile = async (): Promise<any> => {
    try {
      const res = await fetch('/api/integrators/profile', {
        method: 'GET',
      });

      if (!res.ok) {
        setLoading(false);
        if (res.status === 401 || res.status === 403 || res.status === 404) {
          router.push('/login');
        }
        return null;
      }

      const profile = await res.json();
      setIntegratorProfile(profile);
      setLoading(false);
      return profile;
    } catch (err: any) {
      console.warn('[fetchIntegratorProfile] Error:', err?.message);
      setLoading(false);
      router.push('/login');
      return null;
    }
  };

  useEffect(() => {
    // Only attempt to fetch the profile if the user has a session cookie.
    // bears_logged_in is a non-HttpOnly cookie set by the login/google routes.
    const isLoggedIn =
      typeof document !== 'undefined' &&
      document.cookie.split(';').some((c) => c.trim().startsWith('bears_logged_in='));

    if (isLoggedIn) {
      fetchIntegratorProfile();
    } else {
      setLoading(false);
      const isPublicPath = ['/', '/login', '/sign-up-login-screen'].includes(
        window.location.pathname
      );
      if (!isPublicPath) {
        router.push('/login');
      }
    }

    if (typeof window !== 'undefined' && navigator.permissions) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((permissionStatus) => {
          permissionStatus.onchange = () => {
            if (permissionStatus.state === 'granted') {
              getGeolocationSilently().then((coords) => {
                if (coords) {
                  fetch('/api/users/me/coordinates', {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ latitude: coords.lat, longitude: coords.lng }),
                  }).catch((e) => console.warn('[GPS Change] Error updating coordinates:', e));
                }
              });
            }
          };
        })
        .catch((e) => console.warn('Permissions query failed:', e));
    }
  }, []);

  const signInWithBearsAPI = async (
    email: string,
    password: string,
    latitude: number | null = null,
    longitude: number | null = null
  ) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, latitude, longitude }),
    });

    let data = await res.json();

    if (!res.ok) {
      const error: any = new Error(data?.error ?? 'Falha no login. Verifique suas credenciais.');
      error.status = res.status;
      throw error;
    }

    setBearsTokenState('cookie-managed');

    // Save data from login if it contains profile info
    if (data) {
      const profileToSave = data.user || data;
      if (!profileToSave.email && email) profileToSave.email = email;

      setIntegratorProfile(profileToSave);
    }

    const profile = await fetchIntegratorProfile();
    trackLogin('email');
    if (profile) setUserProperties(profile.id || profile.email, profile.role);

    return data;
  };

  // Maps signIn directly to our API login
  const signIn = signInWithBearsAPI;

  const processGoogleLogin = async (idToken: string) => {
    let coords = null;
    try {
      coords = await getGeolocationSilently();
    } catch (e) {
      console.warn('Geolocation error:', e);
    }

    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idToken,
        provider: 'GOOGLE',
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
      }),
    });

    let data: any = {};
    try {
      const text = await res.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch (e) {
      console.warn('[processGoogleLogin] Response was not valid JSON (HTML/Proxy error)');
    }

    if (!res.ok) {
      console.warn('[processGoogleLogin] BEARS API error:', data?.error || res.statusText);
      const fallbackMsg =
        res.status === 504
          ? 'O servidor backend não respondeu a tempo (Gateway Timeout 504).'
          : 'Falha na autenticação com Google.';
      const error: any = new Error(data?.error || fallbackMsg);
      error.status = res.status;
      throw error;
    }

    setBearsTokenState('cookie-managed');

    if (data) {
      const profileToSave = data.user || data;
      setIntegratorProfile(profileToSave);
    }

    const profile = await fetchIntegratorProfile();
    trackLogin('google');
    if (profile) setUserProperties(profile.id || profile.email, profile.role);

    return data;
  };

  const registerWithBearsAPI = async (payload: any) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    let data: any;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error('Falha ao processar resposta do servidor.');
    }

    if (!res.ok) {
      const error: any = new Error(data?.error ?? 'Falha ao criar conta na API.');
      error.status = res.status;
      throw error;
    }

    setBearsTokenState('cookie-managed');

    if (data) {
      const profileToSave = data.user || data;
      if (!profileToSave.email && payload.email) profileToSave.email = payload.email;

      setIntegratorProfile(profileToSave);
    }

    const profile = await fetchIntegratorProfile();
    trackSignUp('email');
    if (profile) setUserProperties(profile.id || profile.email, profile.role);

    return data;
  };

  const signOut = async () => {
    trackLogout();
    setBearsTokenState(null);
    setIntegratorProfile(null);
    await fetch('/api/auth/logout', { method: 'POST' });
  };

  const updateCoordinates = async (latitude: number, longitude: number) => {
    try {
      const res = await fetch('/api/users/me/coordinates', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude, longitude }),
      });
      if (!res.ok) {
        console.warn('[updateCoordinates] Failed to update coordinates');
      }
    } catch (e) {
      console.warn('[updateCoordinates] Error updating coordinates:', e);
    }
  };

  const value = {
    loading,
    bearsToken,
    integratorProfile,
    signIn,
    processGoogleLogin,
    signInWithBearsAPI,
    registerWithBearsAPI,
    signOut,
    getBearsToken,
    fetchIntegratorProfile,
    updateCoordinates,
  };

  return (
    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};
